import type { ImageFile } from './types'

/**
 * Session identity and the offline-derivable share link.
 *
 * The WPF app could only build a QR after Google Drive confirmed the upload:
 * it slept up to 7.5s, walked the folder tree by path, shared the folder, then
 * shortened the resulting URL. On a bad venue connection that step fails, and a
 * failed QR blocks the print.
 *
 * Here the short code is derived from the session key with no network at all,
 * so the QR is ready the instant the session exists. The backend row that maps
 * that code to the Drive folder is created when connectivity allows; the guest
 * sees a "still uploading" page until then.
 */

/** Unix milliseconds, as a string. Names the session folder and the composite. */
export function newSessionKey(now: number = Date.now()): string {
  return String(now)
}

/**
 * `c` + base36(sessionKey). Short, collision-free (the key is a timestamp), and
 * computable on both the agent and the PWA without agreeing on anything else.
 */
export function shortCodeFor(sessionKey: string): string {
  return `c${Number(sessionKey).toString(36)}`
}

export function shareUrlFor(sessionKey: string, frontendBase: string): string {
  return `${frontendBase.replace(/\/+$/, '')}/s/${shortCodeFor(sessionKey)}`
}

/**
 * Characters Windows rejects in a file name.
 *
 * The original walked `Path.GetInvalidFileNameChars()` replacing each with `_`,
 * so an explicit set is a closer translation than a regex — and it keeps the
 * control-character range readable.
 */
const INVALID_FILENAME_CHARS = new Set(['<', '>', ':', '"', '/', '\\', '|', '?', '*'])

export function sanitizeFileName(name: string): string {
  let out = ''
  for (const ch of name) {
    out += INVALID_FILENAME_CHARS.has(ch) || ch.charCodeAt(0) < 32 ? '_' : ch
  }
  return out
}

/** The gallery shows newest first (`OrderByDescending(f => f.CreatedAt)`). */
export function sortForGallery(files: ImageFile[]): ImageFile[] {
  return [...files].sort((a, b) => b.createdAt - a.createdAt)
}

/** Identity is the file path, compared case-insensitively as the HashSets did. */
export function dedupeByPath(files: ImageFile[]): ImageFile[] {
  const seen = new Set<string>()
  const out: ImageFile[] = []
  for (const f of files) {
    const key = f.filePath.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(f)
  }
  return out
}

/** GUID with dashes stripped, matching `Guid.NewGuid().ToString().Replace("-","")`. */
export function newIdentifier(): string {
  return crypto.randomUUID().replace(/-/g, '')
}

export const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg'] as const

export function isSupportedImage(path: string): boolean {
  const lower = path.toLowerCase()
  return IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext))
}
